// utils/api.js - Axios API client
import axios from 'axios';
import { nativeConfigure } from './nativeBridge';

const BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// A tablet that was closed for more than a day starts with an expired token.
// Renew it before any other request goes out: otherwise the first GET returns 401,
// the interceptor below logs the tablet out and the offline queue sits until
// someone logs in again. One refresh is shared by all requests waiting on it.
let refreshing = null;

function tokenExp(token) {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' ? exp : 0;
  } catch {
    return 0;
  }
}

function tokenExpired(token) {
  const exp = tokenExp(token);
  return !!exp && exp * 1000 < Date.now() + 60000;
}

// Ask the server for a fresh token. One refresh is shared by everything waiting
// on it. Resolves true when a new token was stored. While the device is offline
// or the network is flaky this simply resolves false: the session stays and the
// next attempt comes after a short pause, so offline work is not held up by a
// refresh timing out in front of every request. navigator.onLine is deliberately
// not consulted: in the tablet WebView it reports offline while the server is
// reachable, and a tablet that believed it would never renew its token.
const REFRESH_RETRY_MS = 30000;
let refreshFailedAt = 0;

export function renewToken({ force = false } = {}) {
  if (refreshing) return refreshing;
  if (!force && Date.now() - refreshFailedAt < REFRESH_RETRY_MS) return Promise.resolve(false);
  refreshing = api.post('/api/auth/refresh', null, { timeout: 10000 })
    .then(() => { refreshFailedAt = 0; return true; })
    .catch(() => { refreshFailedAt = Date.now(); return false; })
    .finally(() => { refreshing = null; });
  return refreshing;
}

// Inject token
api.interceptors.request.use(async config => {
  let token = localStorage.getItem('auth_token');
  if (token && config.url !== '/api/auth/refresh' && tokenExpired(token)) {
    await renewToken();
    token = localStorage.getItem('auth_token');
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// The server renews tokens older than 12h via X-Refresh-Token. Store it and hand
// it to the native uploader, otherwise WorkManager keeps uploading with the token
// from the last login and hits 401 a day later. Only while still logged in, so a
// late response can't resurrect a session after logout. A cached answer (a 304 the
// browser fills from its HTTP cache, or the service worker while offline) carries the
// header from the day it was stored: never trade the current token for an older one.
// On 19-10-2026 that swapped a fresh login for a week-old token and signed people out.
function adoptRefreshedToken(res) {
  const fresh = res?.headers?.['x-refresh-token'];
  const current = localStorage.getItem('auth_token');
  if (fresh && current && tokenExp(fresh) > tokenExp(current)) {
    localStorage.setItem('auth_token', fresh);
    nativeConfigure({ token: fresh });
  }
}

// A 401 on these routes is about the submitted password, not about the session.
const CREDENTIAL_ROUTES = ['/api/auth/login', '/api/auth/change-password'];

// Only an answer from our own server, received while the device is online, counts
// as a rejection. A captive portal, a proxy error or a dropped connection does not.
function isServerRejection(err) {
  return err.response?.status === 401
    && typeof err.response.data?.error === 'string'
    && !(typeof navigator !== 'undefined' && navigator.onLine === false);
}

// A 401 never signs the tablet out by itself: factory wifi drops all day and an
// expired token that could not be renewed in time is not a reason to send someone
// to the office. The request that got the 401 asks for a fresh token and is sent
// once more. Only when the refresh itself is turned away by the server (account
// deactivated, password changed, token too old) does the session end.
api.interceptors.response.use(
  res => { adoptRefreshedToken(res); return res; },
  async err => {
    const config = err.config || {};
    const signedIn = !!localStorage.getItem('auth_user');
    if (err.response?.status !== 401 || !signedIn || CREDENTIAL_ROUTES.includes(config.url)) {
      return Promise.reject(err);
    }
    if (config.url === '/api/auth/refresh') {
      if (isServerRejection(err)) {
        window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'rejected' } }));
      }
      return Promise.reject(err);
    }
    if (config._retriedAfterRefresh || !(await renewToken({ force: true }))) return Promise.reject(err);
    return api({ ...config, _retriedAfterRefresh: true });
  }
);

export default api;

// Helper to extract error message
export function getErrorMessage(err) {
  return err?.response?.data?.error || err?.message || 'An unexpected error occurred';
}
